import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { badge } from '../../shared/badges.ts';
import type { Color, GameResult, GameReward, RatingChange } from '../../shared/protocol.ts';
import { useT } from '../app/preferences.ts';
import type { MessageKey } from '../i18n/messages.ts';
import { sound } from '../lib/sound.ts';
import { GoldAmount } from './PlayerLook.tsx';
import { Dialog } from './Dialog.tsx';

interface ResultDialogProps {
  open: boolean;
  result: GameResult | null;
  /** The viewer's colour, or null for a spectator. */
  perspective: Color | null;
  moveCount: number;
  /** Names shown on the score line ("Ada 1 – 0 Can"). */
  players?: Record<Color, string>;
  /** For a player in a rated game: their rating change, or null when the game did not count. */
  rating?: { change: RatingChange | null };
  /** Gold and new badges the viewer earned from this game. */
  reward?: GameReward | null;
  onClose: () => void;
  /** Buttons; the first one is the main action and spans the full width. */
  actions: ReactNode;
}

type Outcome = 'win' | 'loss' | 'draw' | 'neutral';

/**
 * End-of-game screen: a victory stage with trophy and confetti, a fallen king
 * for a loss, ½ for a draw. Plays the matching sound when it opens.
 */
export function ResultDialog({ open, result, perspective, moveCount, players, rating, reward, onClose, actions }: ResultDialogProps) {
  const t = useT();
  const outcome: Outcome | null = !result
    ? null
    : result.winner === null
      ? 'draw'
      : perspective === null
        ? 'neutral'
        : result.winner === perspective
          ? 'win'
          : 'loss';

  useEffect(() => {
    if (!open || !outcome) return;
    sound.play(outcome === 'win' ? 'win' : outcome === 'loss' ? 'loss' : 'draw');
  }, [open, outcome]);

  if (!result || !outcome) return null;

  const fullMoves = Math.ceil(moveCount / 2);
  const title =
    outcome === 'draw'
      ? t('result.draw')
      : outcome === 'win'
        ? t('result.win')
        : outcome === 'loss'
          ? t('result.loss')
          : result.winner === 'w'
            ? t('result.whiteWins')
            : t('result.blackWins');
  const cheer =
    outcome === 'win' ? t('result.winCheer') : outcome === 'loss' ? t('result.lossCheer') : outcome === 'draw' ? t('result.drawCheer') : null;
  const score = result.winner === null ? ['½', '½'] : result.winner === 'w' ? ['1', '0'] : ['0', '1'];

  return (
    <Dialog open={open} onClose={onClose} title={title} bare footer={actions} className={`result-dialog ${outcome}`}>
      <div className="result-stage">
        {outcome === 'win' && <div className="result-rays" aria-hidden="true" />}
        {outcome === 'win' && <Confetti />}
        <Emblem outcome={outcome} />
        <p className="result-kicker">{t(`reason.${result.reason}`)}</p>
        <h2 className="result-title">{title}</h2>
        {cheer && <p className="result-cheer">{cheer}</p>}
      </div>

      <div className="result-body">
        {players && (
          <div className="result-score" aria-label={`${players.w} ${score[0]} – ${score[1]} ${players.b}`}>
            <span className={`result-side${result.winner === 'w' ? ' won' : ''}`}>
              <span className="vs-color-dot white" aria-hidden="true" />
              <span className="result-side-name">{players.w}</span>
            </span>
            <strong className="result-score-num">
              {score[0]}
              <span aria-hidden="true">–</span>
              {score[1]}
            </strong>
            <span className={`result-side right${result.winner === 'b' ? ' won' : ''}`}>
              <span className="result-side-name">{players.b}</span>
              <span className="vs-color-dot black" aria-hidden="true" />
            </span>
          </div>
        )}
        {rating && <RatingLine change={rating.change} />}
        {reward && (reward.gold > 0 || reward.badges.length > 0) && (
          <div className="result-rewards">
            {reward.gold > 0 && (
              <span className="badge badge-accent reward-gold">
                <GoldAmount amount={reward.gold} signed />
              </span>
            )}
            {reward.badges.map((id) => (
              <span key={id} className="badge badge-green reward-badge">
                <span className="emoji" aria-hidden="true">
                  {badge(id)?.emoji}
                </span>
                {t('reward.newBadge')} {t(`badge.${id}` as MessageKey)}
              </span>
            ))}
          </div>
        )}
        <p className="result-reason">{t(fullMoves === 1 ? 'result.move' : 'result.moves', { count: fullMoves })}</p>
      </div>
    </Dialog>
  );
}

function Emblem({ outcome }: { outcome: Outcome }) {
  if (outcome === 'loss') {
    // The king tips over.
    return (
      <div className="result-emblem" aria-hidden="true">
        <span className="fallen-king">♚</span>
      </div>
    );
  }
  if (outcome === 'draw') {
    return (
      <div className="result-emblem" aria-hidden="true">
        <span className="half">½</span>
      </div>
    );
  }
  return (
    <div className="result-emblem emoji" aria-hidden="true">
      {outcome === 'win' ? '🏆' : '🏁'}
    </div>
  );
}

function RatingLine({ change }: { change: RatingChange | null }) {
  const t = useT();
  const diff = change ? change.after - change.before : 0;
  const shown = useCountUp(diff);
  if (!change) return <p className="result-rating">{t('result.ratingSame')}</p>;
  return (
    <p className="result-rating">
      <span className="emoji" aria-hidden="true">
        🏆
      </span>{' '}
      {t('result.ratingChange', { before: change.before, after: change.after })}{' '}
      <span className={`rating-diff ${diff > 0 ? 'up' : diff < 0 ? 'down' : ''}`}>{shown > 0 ? `+${shown}` : shown}</span>
    </p>
  );
}

/** Counts from 0 to `target` over a moment, for the rating change. */
function useCountUp(target: number, ms = 900): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      setValue(Math.round(target * (1 - (1 - p) ** 3)));
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, ms]);
  return target === 0 ? 0 : value;
}

// Gold and white only: a victory, not a party.
const CONFETTI_COLORS = ['#ffd700', '#ffffff', '#f5b301', '#fff3b0', '#e8a317', '#ffe680'];

/** A burst of falling paper; positions are spread by index so renders stay pure. */
function Confetti() {
  return (
    <div className="confetti" aria-hidden="true">
      {Array.from({ length: 44 }, (_, i) => (
        <i
          key={i}
          style={
            {
              '--x': `${(i * 37) % 100}%`,
              '--c': CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              '--d': `${1.8 + ((i * 7) % 10) / 8}s`,
              '--delay': `${((i * 13) % 12) / 10}s`,
              '--r': `${(i % 2 ? 1 : -1) * (180 + ((i * 53) % 360))}deg`,
              '--w': `${6 + (i % 3) * 3}px`,
            } as CSSProperties
          }
        />
      ))}
    </div>
  );
}
